import React, { useEffect, useRef, useState } from 'react';
import { Button, Spin, Empty } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { get_batch_summary_report_json } from '@/utils/api';
import { REPORT_STYLE, dash, num, dt, printReport } from './reportStyle';

/**
 * Batch Summary — data-driven. Fetches get_batch_summary_report_json and renders
 * from JSON in the backend report design.
 * Shape: { batch_details, process_time_details[], process_time_total,
 *          rm_details[], in_process_quality_details[] }.
 */
const BatchSummaryReport = ({ batchNumber }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    if (!batchNumber) return;
    let active = true;
    setLoading(true); setError(''); setData(null);
    get_batch_summary_report_json(
      { batch_number: batchNumber },
      (res) => {
        if (!active) return;
        if (res?.data?.status === 'success' && res?.data?.data) setData(res.data.data);
        else setError(res?.data?.message || 'No report data available');
        setLoading(false);
      },
      () => { if (active) { setError('Failed to load report'); setLoading(false); } }
    );
    return () => { active = false; };
  }, [batchNumber]);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spin size="large" tip="Generating report…" /></div>;
  if (error || !data) return <Empty description={error || 'No report'} style={{ padding: 60 }} />;

  const {
    batch_details = {},
    process_time_details = [],
    process_time_total = {},
    rm_details = [],
    in_process_quality_details = [],
  } = data;

  return (
    <div>
      <style>{REPORT_STYLE}</style>
      <div className="no-print" style={{ textAlign: 'right', marginBottom: 8 }}>
        <Button type="primary" icon={<PrinterOutlined />} onClick={() => printReport(rootRef.current, batch_details.batch_number)}>
          Print / Download
        </Button>
      </div>

      <div ref={rootRef} className="bmr-report">
        <div className="container">
          <header><h1>Batch Summary Report</h1></header>

          {/* Batch details */}
          <table>
            <tbody>
              <tr><td><b>Location:</b> {dash(batch_details.location)}</td><td><b>Batch No:</b> {dash(batch_details.batch_number)}</td><td><b>Revision No:</b> {dash(batch_details.recipe_revision_no)}</td></tr>
              <tr><td><b>Equipment:</b> {dash(batch_details.equipment_tag)}</td><td><b>From:</b> {dt(batch_details.batch_start_date_time)}</td><td><b>To:</b> {dt(batch_details.to_date_time)}</td></tr>
              <tr><td><b>Batch Size:</b> {dash(batch_details.batch_size)}</td><td colSpan={2}><b>Actual Quantity:</b> {dash(batch_details.actual_quantity)}</td></tr>
            </tbody>
          </table>

          {/* Process time details */}
          <div className="section-title"><table><tbody><tr><th className="header-class">Process Time Details</th></tr></tbody></table></div>
          <table>
            <thead><tr className="header-class-1"><th>Process</th><th>Start</th><th>End</th><th>Standard</th><th>Actual</th><th>Difference</th><th>% Deviation</th></tr></thead>
            <tbody>
              {process_time_details.map((p, i) => (
                <tr key={i}>
                  <td>{dash(p.process_name)}</td>
                  <td className="table_data">{dt(p.start_time)}</td>
                  <td className="table_data">{dt(p.end_time)}</td>
                  <td className="table_data">{dash(p.standard_time)}</td>
                  <td className="table_data">{dash(p.actual_time)}</td>
                  <td className="table_data">{dash(p.difference)}</td>
                  <td className="table_data">{num(p.deviation_percentage)}%</td>
                </tr>
              ))}
              {process_time_total && Object.keys(process_time_total).length > 0 && (
                <tr style={{ fontWeight: 'bold', background: '#eaf1fb' }}>
                  <td>{dash(process_time_total.label) || 'Total'}</td><td></td><td></td>
                  <td className="table_data">{dash(process_time_total.standard_time)}</td>
                  <td className="table_data">{dash(process_time_total.actual_time)}</td>
                  <td className="table_data">{dash(process_time_total.difference)}</td>
                  <td className="table_data">{num(process_time_total.deviation_percentage)}%</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Raw material details */}
          <div className="section-title"><table><tbody><tr><th className="header-class">Raw Material Details</th></tr></tbody></table></div>
          <table>
            <thead><tr className="header-class-1"><th>Material</th><th>Standard</th><th>Actual</th><th>% Deviation</th></tr></thead>
            <tbody>
              {rm_details.map((m, i) => (
                <tr key={i}>
                  <td>{dash(m.rm)}</td>
                  <td className="table_data">{num(m.standard_quantity, 3)}</td>
                  <td className="table_data">{num(m.actual_quantity, 3)}</td>
                  <td className="table_data">{m.deviation_percentage === 'NA' ? 'NA' : `${num(m.deviation_percentage)}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* In-process quality */}
          {in_process_quality_details.map((q, i) => (
            <div key={i}>
              <div className="section-title"><table><tbody><tr><th className="header-class">In-Process Quality — {dash(q.process_name)}</th></tr></tbody></table></div>
              {(q.testing_types || []).map((tt, ti) => (
                <div key={ti}>
                  <div className="section-title" style={{ marginTop: 8 }}><table><tbody><tr><th className="header-class-1">{dash(tt.testing_type)}</th></tr></tbody></table></div>
                  <table>
                    <thead><tr className="header-class-1"><th>S.No</th><th>Parameter</th><th>Min</th><th>Max</th><th>Actual</th></tr></thead>
                    <tbody>
                      {(tt.parameters || []).map((p, pi) => (
                        <tr key={pi}>
                          <td className="table_data">{dash(p.s_no)}</td>
                          <td>{dash(p.parameter)}</td>
                          <td className="table_data">{dash(p.standard_min)}</td>
                          <td className="table_data">{dash(p.standard_max)}</td>
                          <td className="table_data">{dash(p.actual_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BatchSummaryReport;
